/**
 * Группы токенов и порядок их открытия.
 *
 * Механизм тот же, что в «Основах» и режимах символов: сразу весь пакет —
 * это тот же обескураживающий экран, ради ухода от которого материал и
 * делится. Различается состав группы: он задан материалом, а не нарезкой по
 * два, потому что скобка без парной ей скобки бессмысленна.
 *
 * Элемент упражнения — оператор или разделитель целиком (ТЗ FR-EN-21): `!=`,
 * `<=`, `&&` тренируются как одно движение, а не как две отдельные клавиши.
 * Материал набирается в латинской раскладке независимо от раскладки профиля
 * (ТЗ FR-EN-22): ЙЦУКЕН физически не содержит ни собачки, ни скобок.
 */

import type { MasteryUnit } from '@type2x/shared-contracts';
import { toMasteryUnitId } from '@type2x/shared-contracts';
import { getLayout, strokeForCharacter } from '@type2x/keyboard-layouts';
import {
  currentUnlockableGroup,
  masteredGroups,
  unlockedGroups,
  type UnlockableGroup,
} from '@type2x/learning-engine';

import type { PackGroup, PackMode, PackShiftHint, PacksSnapshot } from '../contracts/packs.js';
import { PACK_LAYOUT, PACK_UNIT_PREFIX } from '../contracts/packs.js';
import { packTokenGroups } from './material.js';

/** Идентификатор учебной единицы токена. */
export function packUnitId(token: string): string {
  return `${PACK_UNIT_PREFIX}${token}`;
}

/**
 * Учебная единица токена.
 *
 * Вид единицы — `pattern`: `!=` не клавиша и не слово, а устойчивое
 * сочетание. Частота в реальном языке нулевая, и это не заглушка: эти
 * сочетания живут в коде и адресах, а не в тексте, и частотный словарь языка
 * о них ничего не знает.
 */
export function packUnit(snapshot: PacksSnapshot, token: string): MasteryUnit {
  return {
    id: toMasteryUnitId(packUnitId(token)),
    kind: 'pattern',
    content: token,
    language: snapshot.language,
    layoutId: PACK_LAYOUT,
    realWorldFrequency: 0,
  };
}

/** Группа в виде, понятном общему механизму открытия. */
function unlockable(group: PackGroup): UnlockableGroup {
  return {
    id: group.id,
    unitIds: group.tokens.map((token) => toMasteryUnitId(packUnitId(token))),
  };
}

/** Все группы режима в порядке открытия. */
export function packGroups(mode: PackMode): PackGroup[] {
  return packTokenGroups(mode).map((tokens, index) => ({
    id: `${mode}-${index}`,
    mode,
    tokens,
  }));
}

/** Группы режима, пройденные к этому моменту. */
export function completedGroups(snapshot: PacksSnapshot, mode: PackMode): PackGroup[] {
  const groups = packGroups(mode);
  const mastered = new Set(masteredGroups(groups.map(unlockable), snapshot.stats).map((g) => g.id));
  return groups.filter((group) => mastered.has(group.id));
}

/** Группа, которую пользователь осваивает сейчас; null — пройдены все. */
export function currentGroup(snapshot: PacksSnapshot, mode: PackMode): PackGroup | null {
  const groups = packGroups(mode);
  const current = currentUnlockableGroup(groups.map(unlockable), snapshot.stats);
  return groups.find((group) => group.id === current?.id) ?? null;
}

/** Токены режима, доступные упражнению: пройденные группы и текущая. */
export function unlockedTokens(snapshot: PacksSnapshot, mode: PackMode): string[] {
  const groups = packGroups(mode);
  const open = new Set(unlockedGroups(groups.map(unlockable), snapshot.stats).map((g) => g.id));

  return groups.filter((group) => open.has(group.id)).flatMap((group) => group.tokens);
}

/** Есть ли режиму чему учить: остались непройденные группы. */
export function hasPack(snapshot: PacksSnapshot, mode: PackMode): boolean {
  return currentGroup(snapshot, mode) !== null;
}

/**
 * Подсказки о Shift для символов текста.
 *
 * На латинской раскладке под Shift лежит половина пакета — `{`, `}`, `<`,
 * `>`, `&`, `|`, `_`, `@`, `"`. Показать только сам символ значит отправить
 * пользователя искать его глазами по всей клавиатуре (ТЗ §6, режим 10).
 */
export function shiftHints(characters: Iterable<string>): PackShiftHint[] {
  const layout = getLayout(PACK_LAYOUT);
  const hints = new Map<string, PackShiftHint>();

  for (const character of characters) {
    if (hints.has(character)) continue;

    const stroke = strokeForCharacter(layout, character);
    if (stroke === null || !stroke.requiresShift) continue;

    hints.set(character, {
      character,
      base: stroke.key.base,
      finger: stroke.key.finger,
    });
  }

  return [...hints.values()];
}
